/**
 * Pryme protocol v1 — shared wire vocabulary.
 * Normative description: docs/PROTOCOL.md. Machine-readable API: openapi.json.
 */
export const PROTOCOL_VERSION = 1;
export const REQUEST_SIG_VERSION = 'pryme-request-v1';
/** JWS `typ` values. Every verifier MUST check typ (domain separation). */
export const TYP = {
    /**
     * Pryme's attestation issuer certifies that a pairwise key represents a verified, active Pryme
     * identity for one audience (a service). Not signed with any service credential (ADR-0014).
     */
    binding: 'pryme-binding-v2',
    /** Pairwise key signs derived claim state for one recipient. */
    state: 'pryme-state-v1',
    /** Pairwise key signs a delegation to an agent principal. */
    mandate: 'pryme-mandate-v1',
    /** Pairwise key signs an authorization decision for one exact operation. */
    decision: 'pryme-decision-v1',
    /** Service A key signs a gateway response to a signed service request (the service channel only). */
    response: 'pryme-response-v1',
    /** Pairwise key signs the human's approval of one exact escalated operation. */
    approval: 'pryme-approval-v1',
    /**
     * The agent's own key signs the exact operation it asks one recipient to perform (ADR-0015).
     * Without it, a check is the service's own request: it may ask the human, never use the agent's permission.
     * Pryme's own EdDSA JWS type: not an AP2 mandate, and never accepted where one is expected (or vice versa).
     */
    intent: 'pryme-intent-v1',
};
export const HEADERS = {
    service: 'pryme-service',
    agent: 'pryme-agent',
    keyId: 'pryme-key-id',
    audience: 'pryme-audience',
    timestamp: 'pryme-timestamp',
    nonce: 'pryme-nonce',
    digest: 'pryme-content-digest',
    signature: 'pryme-signature',
    binding: 'pryme-binding',
    mandate: 'pryme-mandate',
    approval: 'pryme-approval',
    /** The agent's signed intent (`pryme-intent-v1`) for the operation this request asks for. */
    intent: 'pryme-intent',
    responseSignature: 'pryme-response-signature',
    stepUp: 'pryme-step-up',
};
/** Maximum clock skew / signed-request lifetime in seconds. */
export const REQUEST_MAX_AGE_SECONDS = 300;
export const ERROR_CODES = [
    'identity_not_verified',
    'verification_pending',
    'verification_denied',
    'verification_expired',
    'verification_provider_unavailable',
    'unsupported_verification_scheme',
    'assurance_insufficient',
    'duplicate_identity_review_required',
    'invalid_signature',
    'unregistered_service',
    'unverified_domain',
    'wrong_audience',
    'replay_detected',
    'expired_presentation',
    'unknown_pairwise_identity',
    'consent_required',
    'claim_not_disclosed',
    'issuer_not_verified',
    'mandate_revoked',
    'mandate_expired',
    'constraint_violation',
    'approval_required',
    'approval_denied',
    'credential_revoked',
    'identity_recovery_required',
    'rate_limited',
    'temporarily_unavailable',
    // PoC extensions
    'invalid_request',
    'invalid_state',
    'mandate_suspended',
    'agent_not_authorized',
    'handle_unavailable',
    'step_up_required',
    'forbidden',
    'not_found',
];
export const SUPPORTED_CLAIMS = [
    'human.verified',
    'age.over13',
    'age.over15',
    'age.over16',
    'age.over18',
    'age.over21',
    /**
     * The country of the eID scheme Pryme verified (MitID → `DK`). Not nationality, not residence.
     * Replaces `residency.country` (removed 2026-11, security review L11).
     */
    'eid.scheme_country',
];
export * from './interop.js';
// ---------------------------------------------------------------- money (security review 2026-11, H2)
/**
 * The currencies Pryme supports and their ISO 4217 minor units. Amounts are always integer minor
 * units of one of these. The Rust core (`money::exponent`) is authoritative; this table mirrors it and
 * `tests/unit/money.test.mjs` checks every entry, and some codes that must stay unsupported, against it.
 * Nothing in Pryme may assume decimals any other way.
 */
export const CURRENCY_EXPONENTS = Object.freeze({
    DKK: 2, EUR: 2, SEK: 2, NOK: 2, USD: 2, GBP: 2, CHF: 2, PLN: 2, ISK: 0, JPY: 0,
});
/** Minor-unit exponent of a supported currency; null for anything else. */
export function currencyExponent(currency) {
    return typeof currency === 'string' && Object.prototype.hasOwnProperty.call(CURRENCY_EXPONENTS, currency) ? CURRENCY_EXPONENTS[currency] : null;
}
/** Largest amount Pryme accepts, in minor units (Rust `money::MAX_MINOR`). */
export const MAX_MINOR = 100_000_000_000;
/**
 * Integer minor units → exact decimal string, as the Rust core's `money_format` ("12345", DKK → "123.45";
 * 5000, JPY → "5000"). Integer arithmetic only. Null for an unsupported currency or a value that is not a
 * non-negative safe integer.
 */
export function minorToDecimal(minor, currency) {
    const exp = currencyExponent(currency);
    if (exp === null || !Number.isSafeInteger(minor) || minor < 0)
        return null;
    if (exp === 0)
        return String(minor);
    const digits = String(minor).padStart(exp + 1, '0');
    return `${digits.slice(0, -exp)}.${digits.slice(-exp)}`;
}
/**
 * Decimal string → integer minor units, with exactly the Rust core's `money::parse_decimal` grammar:
 * digits, an optional "." with at most the currency's exponent of digits, no leading zeros, no sign or
 * exponent, at most `MAX_MINOR`. Null if invalid.
 */
export function decimalToMinor(amount, currency) {
    const exp = currencyExponent(currency);
    if (exp === null)
        return null;
    const m = /^(0|[1-9]\d{0,11})(?:\.(\d+))?$/.exec(amount);
    if (!m || (m[2] !== undefined && m[2].length > exp))
        return null;
    const minor = Number(m[1]) * 10 ** exp + Number((m[2] ?? '').padEnd(exp, '0') || '0');
    return minor <= MAX_MINOR ? minor : null;
}
/**
 * The canonical input of an operation hash (security review I8): JCS (RFC 8785) of exactly these keys,
 * absent fields as `null`, empty strings as absent (the gateway drops them). The hash is
 * base64url(SHA-256(that UTF-8 string)), no padding: `packages/protocol/fixtures/signature-vectors.json`
 * (`operation_hash`) has vectors from the Rust core.
 */
export function operationHashInput(mandateId, recipient, agentId, ctx) {
    const s = (v) => (typeof v === 'string' && v !== '' ? v : null);
    return {
        v: 1, mandate: mandateId, recipient, agent: agentId, action: ctx.action,
        amount: typeof ctx.amount === 'number' ? ctx.amount : null,
        currency: s(ctx.currency), resource: s(ctx.resource), merchant: s(ctx.merchant), orderId: s(ctx.orderId),
    };
}
/** Actions that move money and so must always state an amount and currency (Rust `mandate::is_monetary`). */
export const isMonetaryAction = (action) => action.startsWith('payment.') || action.split('.').pop() === 'purchase';
